import React from 'react';
import styled from 'styled-components/macro';
import first from 'lodash.first';

import { Link } from 'components/data-containers/PositionsTable/shared/PositionsTable.style';
import WithLabel from 'components/data-visualization/Table/WithLabel';

import formatNumber from 'helpers/formatNumber';
import getShortName from 'helpers/getShortName';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import CountryFlag from 'components/base/CountryFlag/index';
import CustomerIcon from 'components/base/CustomerIcon/CustomerIcon';
import PositionArrow from 'components/utility/PositionArrow/PositionArrow';
import TooltipWrapper from 'components/base/TooltipWrapper';
import { FixedWidthPosition, TitleWithIcon } from '../shared/Charts/styles';
import { TableRow as BreakdownTableRow } from 'components/Overview/BreakdownTable/styles';
import theme from 'styles/themes/variables';

import { IChartItem } from './types';
import { MAX_COUNTRY_NAME } from 'config';

const TableRow = styled(BreakdownTableRow)`
  td {
    padding: ${({ theme }): string => `0 ${theme.spacing.xsmall} 0`};
    @media screen and (max-width: 50em) {
      padding-top: 0.5em;
      padding-bottom: 1em;
    }
  }

  @media screen and (max-width: 50em) {
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid ${({ theme }): string => theme.Grey};

    td {
      flex: 1 1 50%;

      &[data-product-column] {
        order: 3;
      }
    }
  }
`;

interface IDSPTableRowProps {
  chart: IChartItem;
  productType: string;
  isSmallScreen: boolean;
  showProductColumn: boolean;
}

const DSPTableRow: React.FC<IDSPTableRowProps> = ({
  chart,
  productType,
  isSmallScreen,
  showProductColumn,
}) => {
  const entityType = apiTypeToEntityType(chart.productType);

  const {
    productDisplayName,
    productId,
    chartDisplayName,
    currentPosition,
    customerName,
  } = chart;

  const territory = first(chart.territoryData);
  const territoryDisplayName = isSmallScreen
    ? territory?.id
    : territory?.displayName || 'Global';
  const previousPosition = territory?.previousPosition;
  const territoryName = getShortName(territoryDisplayName);

  return (
    <TableRow>
      <td>
        <FixedWidthPosition margin={`0 ${theme.spacing.xsmall} 0 0`}>
          {formatNumber(currentPosition, 'position')}
        </FixedWidthPosition>
        <PositionArrow
          position={currentPosition}
          previousPosition={previousPosition}
          showTooltip={true}
        ></PositionArrow>
      </td>
      {showProductColumn && (
        <td data-product-column>
          <WithLabel
            shouldDisplayLabel={isSmallScreen}
            label={`${productType ? productType : ''} name`}
          >
            <Link to={`/entity/${entityType}/${productId}/charts`}>
              {productDisplayName}
            </Link>
          </WithLabel>
        </td>
      )}
      <td>
        <WithLabel shouldDisplayLabel={isSmallScreen} label="chart">
          <TitleWithIcon>
            <CustomerIcon
              small={isSmallScreen}
              showHiddenPlaceholder
              aria-hidden
              customer={customerName}
            />
            {chartDisplayName}
          </TitleWithIcon>
        </WithLabel>
      </td>
      <td>
        <TooltipWrapper
          tooltipContent={territoryDisplayName}
          condition={(territoryDisplayName || '')?.length > MAX_COUNTRY_NAME}
        >
          <WithLabel shouldDisplayLabel={isSmallScreen} label="territory">
            <CountryFlag
              id={territory?.countryId as string}
              displayName={territory?.displayName as string}
              flagImgSmall={territory?.flagImgSmall as string}
              flagImgMedium={territory?.flagImgMedium as string}
              flagImgLarge={territory?.flagImgLarge as string}
              small
            />
            <span>
              {territory?.countryId === 'WW' ? 'Global' : territoryName}
            </span>
          </WithLabel>
        </TooltipWrapper>
      </td>
    </TableRow>
  );
};

export default DSPTableRow;
